import { OptionIcon } from '@app/src/components/Form/Select'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import type { Option } from '@app/src/components/Form/Select/SimpleSelect'
import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import { ResponseItemView } from '@app/src/context/ResponseItemContextProvider'
import useQuestionRules from '@app/src/hooks/useQuestionRules'
import { PreviewMode } from '@app/src/pages/Questionnaire/Provider/Report/EditResponse'
import type { RequestItem } from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { Stack, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { AiSuggestion } from '../../hooks/useFetchFileAiSuggestions'
import QuestionSuggestionFooter from './SuggestedAnswers/QuestionSuggestionFooter'

type OptionSelectWithSuggestionProps = {
  requestItem: RequestItem
  disabled?: boolean
  hasError?: boolean
  error?: string
  previewMode?: PreviewMode
}

const OptionSelectWithSuggestion: React.FC<OptionSelectWithSuggestionProps> = ({
  requestItem,
  disabled,
  hasError,
  error,
  previewMode,
}) => {
  const { control, clearErrors, setValue, trigger } = useFormContext()
  const { formatMessage } = useIntl()
  const { hideInputField, setResponseItemView, previousAnswer, setQuestionSkippedFields } = useResponseItemContext()
  const [appliedValue, setAppliedValue] = useState<string[]>()
  const { rulesTriggeredByCurrentQuestion, applySkipRuleEffectsToQuestion } = useQuestionRules(requestItem)

  const [options, setOptions] = useState<Option[]>(() =>
    requestItem.requestCollection?.items?.map(option => ({
      value: option.value,
      label: option.displayText,
    })),
  )

  const transformedOptions =
    previewMode === PreviewMode.SelectionDisabled ? options.map(op => ({ ...op, disabled: true })) : options

  const name = `${requestItem.id}.selectedOptions`
  const selectedValue: string[] | string | undefined = useWatch({ name })

  useEffect(() => {
    if (!appliedValue || !selectedValue) return
    const selectedValueArray = Array.isArray(selectedValue) ? selectedValue : [selectedValue]
    const sameAsAppliedValue =
      selectedValueArray.length === appliedValue.length && selectedValueArray.every(s => appliedValue.includes(s))
    if (sameAsAppliedValue) return

    setAppliedValue(undefined)
    setResponseItemView(prevState =>
      prevState === ResponseItemView.SuggestedAnswerAccepted ? ResponseItemView.ShowSuggestedAnswer : prevState,
    )
  }, [Array.isArray(selectedValue) ? selectedValue.join(',') : selectedValue])

  useEffect(() => {
    if (!rulesTriggeredByCurrentQuestion?.length) return

    if (!selectedValue || (Array.isArray(selectedValue) && selectedValue.length === 0)) return

    const hasRulesWithTargets = rulesTriggeredByCurrentQuestion.some(rule => rule.sectionQuestionTargets.length > 0)

    if (hasRulesWithTargets) {
      applySkipRuleEffectsToQuestion(requestItem.questionId, selectedValue)
    }
  }, [selectedValue, rulesTriggeredByCurrentQuestion, requestItem.questionId, applySkipRuleEffectsToQuestion])

  const applySuggestion = (optionValues: string[]) => {
    setValue(name, optionValues)
    setAppliedValue(optionValues)
  }

  const onApplyAiSuggestions = useCallback(
    (suggestion: AiSuggestion) => {
      setResponseItemView(ResponseItemView.SuggestedAnswerAccepted)

      if (suggestion.cannotAnswer) {
        setQuestionSkippedFields(AnswerStatus.NotAvailable)
        return
      }

      const suggestedValues = new Set(suggestion.selectedOptions?.map(option => option.toLowerCase()) ?? [])
      applySuggestion(
        requestItem.requestCollection?.items
          ?.map(option => option.value.toString())
          .filter(value => suggestedValues.has(value.toLowerCase())) ?? [],
      )
    },
    [requestItem.requestCollection?.items, setResponseItemView, applySuggestion],
  )

  const onAiSuggestionsFetched = useCallback(
    (suggestion: AiSuggestion) => {
      const mappedOptions: Option[] = requestItem.requestCollection?.items?.map(option => ({
        value: option.value,
        label: option.displayText,
      }))

      const suggestedOptions = new Set(suggestion.selectedOptions?.map(op => op.toLowerCase()) ?? [])

      const optionsWithSuggestions = mappedOptions
        ?.map(option => {
          if (suggestedOptions.has(option.value.toString().toLowerCase())) {
            return {
              ...option,
              additionalText: formatMessage({ id: 'suggestedAnswers.suggestedByAi' }),
            }
          }
          return option
        })
        ?.sort((a, b) => {
          if (a.additionalText && !b.additionalText) return -1
          if (!a.additionalText && b.additionalText) return 1
          return 0
        })

      setOptions(optionsWithSuggestions)
    },
    [
      formatMessage,
      requestItem.requestCollection?.items,
      setOptions,
      transformedOptions,
      setResponseItemView,
      applySuggestion,
    ],
  )

  const applyPreviousAnswer = async () => {
    if (!previousAnswer) return
    if (previousAnswer.cannotAnswer) {
      setQuestionSkippedFields()
    } else {
      applySuggestion(previousAnswer?.selectedOptions)
    }

    setResponseItemView(ResponseItemView.SuggestedAnswerAccepted)
    await trigger()
  }

  const onIgnoreSuggestion = () => {
    setValue(name, [])
    setValue(`${requestItem.id}.cannotAnswer`, false)
    setValue(`${requestItem.id}.answerStatus`, AnswerStatus.Answered)
    setAppliedValue(undefined)
    setResponseItemView(ResponseItemView.ShowSuggestedAnswer)
  }

  return (
    <>
      <Stack spacing={3} display={hideInputField ? 'none' : undefined} px={4} pb={previewMode ? 0 : 2}>
        <Select<string | number>
          multiple={requestItem.allowMultiChoice}
          enableAutoSelect
          disabled={disabled}
          name={name}
          fieldLabel={formatMessage({
            id: requestItem.allowMultiChoice ? 'reporting.selectMultiSelect' : 'reporting.selectSingleSelect',
          })}
          helperText={requestItem.isRequired ? formatMessage({ id: 'reporting.requiredField' }) : undefined}
          control={control}
          error={error}
          size="small"
          renderOption={(props, option, { selected }): JSX.Element => (
            <li {...props}>
              <OptionIcon selected={selected} multiple={requestItem.allowMultiChoice} />
              <Stack>
                <Typography>{option.label}</Typography>
                <Typography variant="caption" color="communication.dark">
                  {option.additionalText}
                </Typography>
              </Stack>
            </li>
          )}
          findSelectedValue={(value, option): boolean => {
            const flatValue = [value].flat()

            return requestItem.allowMultiChoice ? flatValue.includes(option.value) : option.value === flatValue[0]
          }}
          options={transformedOptions}
          onClick={(): void => {
            if (hasError) clearErrors(`${requestItem.id}`)
          }}
        />
      </Stack>
      <QuestionSuggestionFooter
        requestItem={requestItem}
        questionType="options"
        onApplyPreviousAnswer={applyPreviousAnswer}
        onApplyAiSuggestion={onApplyAiSuggestions}
        onAiSuggestionsFetched={onAiSuggestionsFetched}
        onIgnoreSuggestion={onIgnoreSuggestion}
        isPreview={Boolean(previewMode)}
      />
    </>
  )
}

export default OptionSelectWithSuggestion
